import { supabase } from '@/integrations/supabase/client';
import { findLinkedBotInfo } from '@/services/shadow-bot-utils';
import { MergeAnalysisService, EquityPoint } from '@/services/merge-bots/merge-analysis-service';
import { normalizeInstrument, createInstrumentMapping, getChildBotIds } from '@/utils/instrument-utils';
import { createChildSettingsMap } from '@/utils/child-settings-utils';

export interface GrowthDataPoint {
  tradeNumber: number;
  growth: number;
  dollarAmount: number;
  prediction: 'WIN' | 'LOSS';
  actual: number;
  change: number;
  isCorrect: boolean;
  cumulative: number;
  cumulativeDollar: number;
  source: 'backtest' | 'realtime' | 'merged';
  timestamp?: string;
  instrument?: string; // Per-point instrument information
}

export interface PropBotGrowthData {
  originalBot: {
    growthData: GrowthDataPoint[];
    stats: GrowthStats;
  };
  shadowBot: {
    growthData: GrowthDataPoint[];
    stats: GrowthStats;
  };
  hasLinkedBot: boolean;
  linkedBotType: 'original' | 'shadow' | null;
  merged?: {
    growthData: GrowthDataPoint[];
    stats: GrowthStats;
    childCount?: number;
    instrument?: string;
  };
  hasMergedAnalysis?: boolean;
}

export interface GrowthStats {
  finalGrowth: number;
  finalDollarAmount: number;
  maxGrowth: number;
  minGrowth: number;
  maxDollarAmount: number;
  minDollarAmount: number;
  totalTrades: number;
  wins: number;
  losses: number;
  winRate: number;
  totalRange: number;
  maxDrawdown: number;
  maxDrawdownDollar: number;
  finalProfit?: number;
}

interface BacktestPosition {
  position: number;
  prediction: 'WIN' | 'LOSS' | 'SKIP';
  actualResult: number;
  isCorrect?: boolean;
}

interface RealtimeTrade {
  predicted_result: string;
  win: number;
  timestamp: string;
}

export class PropBotGrowthService {
  /**
   * Get prop details from the original bot (handles shadow bots correctly)
   */
  private static async getPropDetails(botId: string): Promise<{
    winPercentage: number;
    lossPercentage: number;
    minCapital: number;
  }> {
    try {
      // Find the original bot (shadow bots should use original bot's prop details)
      const linkedInfo = await findLinkedBotInfo(botId);
      const originalBotId = linkedInfo.currentBotType === 'shadow' ? linkedInfo.linkedBotId : botId;
      const queryBotId = originalBotId || botId;

      const { data: bot } = await supabase
        .from('bots')
        .select(`
          propDetails:bot_prop_details(
            win_percentage,
            loss_percentage,
            min_capital
          )
        `)
        .eq('id', queryBotId)
        .eq('type', 'prop_bot')
        .single();

      if (bot?.propDetails?.[0]) {
        const details = bot.propDetails[0];
        return {
          winPercentage: details.win_percentage ?? 2.0,
          lossPercentage: details.loss_percentage ?? 1.5,
          minCapital: Number(details.min_capital) ?? 100000
        };
      }
    } catch (error) {
      console.error('Error fetching prop details:', error);
    }

    // Fallback to default values
    console.log('DEBUG: Using fallback values');
    return {
      winPercentage: 2.0,
      lossPercentage: 1.5,
      minCapital: 100000
    };
  }


  /**
   * Fetch realtime EXIT events and convert to growth points
   */
  private static async fetchRealtimeGrowthPoints(
    allowedBotIds: string[],
    instrumentMap: Map<string, { childBotId: string; childBotName?: string }>,
    childSettingsMap: Map<string, { tp_usd: number; sl_usd: number; quantity: number }>,
    lastTradeNumber: number,
    lastCumulative: number,
    lastCumulativeDollar: number
  ): Promise<GrowthDataPoint[]> {
    try {
      if (allowedBotIds.length === 0) {
        return [];
      }

      console.log('Fetching realtime growth points for allowed bots:', allowedBotIds);
      
      const { data: exitEvents, error } = await supabase
        .from('bot_signal_events')
        .select('*')
        .in('bot_id', allowedBotIds)
        .in('action', ['EXIT_LONG', 'EXIT_SHORT'])
        .order('timestamp', { ascending: true }); // Ascending for cumulative calculation

      if (error) {
        console.error('Error fetching realtime exit events for growth:', error);
        return [];
      }

      console.log('Found realtime exit events for growth:', exitEvents?.length || 0);

      let cumulativeGrowth = lastCumulative;
      let cumulativeDollar = lastCumulativeDollar;
      let tradeNumber = lastTradeNumber;

      const realtimeGrowthPoints: GrowthDataPoint[] = (exitEvents || [])
        .map(event => {
          const normalizedInstrument = normalizeInstrument(event.instrument);
          
          // Check if this instrument belongs to our merged analysis (relax if map is empty)
          if (instrumentMap.size > 0) {
            const fallbackInstrument = normalizedInstrument.replace(/USD/g, '');
            const hasMapping = instrumentMap.has(normalizedInstrument) || 
                              (fallbackInstrument && instrumentMap.has(fallbackInstrument));
            
            if (!hasMapping) {
              console.log('Skipping growth event with unmapped instrument:', event.instrument, 'Available instruments:', Array.from(instrumentMap.keys()));
              return null;
            }
          }

          tradeNumber++;
          // Handle different win field formats (0, 1, '1', true, false)
          const winValue = event.win;
          const isWin = winValue === true || (typeof winValue === 'number' && winValue === 1) || (typeof winValue === 'string' && winValue === '1');
          const change = isWin ? 1 : -1;
          
          // Get child bot settings for this instrument or bot
          const mappingInfo = instrumentMap.get(normalizedInstrument) || instrumentMap.get(normalizedInstrument.replace(/USD/g, ''));
          const childBotId = mappingInfo?.childBotId || event.bot_id;
          const childSettings = childSettingsMap.get(childBotId);
          
          console.log(`Growth dollar change calculation for ${event.instrument} (${normalizedInstrument}):`, {
            childBotId,
            hasChildSettings: !!childSettings,
            childSettings,
            amount: event.amount,
            isWin
          });
          
          // Calculate dollar change using TP/SL and quantity
          let dollarChange = 0;
          if (childSettings && event.amount && childSettings.quantity > 0) {
            const coefficient = event.amount / childSettings.quantity;
            dollarChange = isWin 
              ? coefficient * childSettings.tp_usd
              : -coefficient * childSettings.sl_usd;
            
            console.log(`Using child settings for growth calculation:`, {
              coefficient,
              tp_usd: childSettings.tp_usd,
              sl_usd: childSettings.sl_usd,
              dollarChange
            });
          } else {
            // Fallback to fixed values if no child settings found
            dollarChange = isWin ? 20 : -20;
            console.warn(`Using fallback dollar change calculation (±$20) for growth bot ${childBotId}. Missing:`, {
              hasChildSettings: !!childSettings,
              hasAmount: !!event.amount,
              validQuantity: childSettings?.quantity > 0
            });
          }

          cumulativeGrowth += change;
          cumulativeDollar += dollarChange;

          return {
            tradeNumber,
            growth: cumulativeGrowth,
            dollarAmount: cumulativeDollar,
            prediction: isWin ? 'WIN' : 'LOSS',
            actual: isWin ? 1 : 0,
            change,
            isCorrect: true, // For realtime, we assume prediction matches outcome
            cumulative: cumulativeGrowth,
            cumulativeDollar,
            source: 'realtime',
            timestamp: event.timestamp,
            instrument: normalizedInstrument
          };
        })
        .filter(point => point !== null) as GrowthDataPoint[];

      console.log('Generated realtime growth points:', realtimeGrowthPoints.length);
      return realtimeGrowthPoints;
    } catch (error) {
      console.error('Error in fetchRealtimeGrowthPoints:', error);
      return [];
    }
  }

  static async fetchMergedGrowth(botId: string): Promise<{ growthData: GrowthDataPoint[]; stats: GrowthStats; childCount: number; instrument?: string } | null> {
    try {
      console.log('fetchMergedGrowth: Looking for data for botId:', botId);
      
      // Priority 1: Check current bot
      let mergedRun = await MergeAnalysisService.getLatestPublishedMergeAnalysis(botId);
      console.log('fetchMergedGrowth: Direct lookup result:', !!mergedRun);
      
      // Priority 2: Check linked bot (original/shadow) immediately
      if (!mergedRun) {
        const linkedInfo = await findLinkedBotInfo(botId);
        console.log('fetchMergedGrowth: LinkedInfo:', linkedInfo);
        if (linkedInfo.linkedBotId) {
          mergedRun = await MergeAnalysisService.getLatestPublishedMergeAnalysis(linkedInfo.linkedBotId);
          console.log('fetchMergedGrowth: Linked bot lookup result:', !!mergedRun);
        }
      }
      
      // Priority 3: Check bot_merge_mapping for current bot as child
      if (!mergedRun) {
        try {
          const { data: mapping } = await supabase
            .from('bot_merge_mapping')
            .select('parent_bot_id')
            .eq('child_bot_id', botId)
            .eq('is_active', true)
            .single();
          
          if (mapping?.parent_bot_id) {
            mergedRun = await MergeAnalysisService.getLatestPublishedMergeAnalysis(mapping.parent_bot_id);
            console.log('fetchMergedGrowth: Mapping lookup result for current bot:', !!mergedRun);
          }
        } catch (error) {
          // Ignore mapping lookup errors
        }
      }
      
      // Priority 4: Check bot_merge_mapping for linked bot as child
      if (!mergedRun) {
        const linkedInfo = await findLinkedBotInfo(botId);
        if (linkedInfo.linkedBotId) {
          try {
            const { data: mapping } = await supabase
              .from('bot_merge_mapping')
              .select('parent_bot_id')
              .eq('child_bot_id', linkedInfo.linkedBotId)
              .eq('is_active', true)
              .single();
            
            if (mapping?.parent_bot_id) {
              mergedRun = await MergeAnalysisService.getLatestPublishedMergeAnalysis(mapping.parent_bot_id);
              console.log('fetchMergedGrowth: Mapping lookup result for linked bot:', !!mergedRun);
            }
          } catch (error) {
            // Ignore mapping lookup errors
          }
        }
      }
      
      if (!mergedRun || !mergedRun.equity_curve || mergedRun.equity_curve.length === 0) {
        return null;
      }
      
      // Get initial equity from first point or 0
      const initialEquity = mergedRun.equity_curve[0]?.equity || 0;
      
      // Convert equity curve to growth data points with proper calculation and timestamps
      const baseGrowthData: GrowthDataPoint[] = mergedRun.equity_curve.map((point: EquityPoint, index: number) => {
        const dollarAmount = point.equity - initialEquity; // Cumulative P&L from start
        const growth = index > 0 ? (point.equity - mergedRun.equity_curve[0].equity) : 0;
        
        return {
          tradeNumber: point.trade_index || index + 1,
          growth,
          dollarAmount,
          change: point.pnl > 0 ? 1 : -1,
          isCorrect: point.pnl > 0,
          actual: point.pnl > 0 ? 1 : 0,
          prediction: point.pnl > 0 ? 'WIN' : 'LOSS',
          cumulative: growth,
          cumulativeDollar: dollarAmount,
          source: 'merged',
          // Use real timestamp from EquityPoint.t if available, fallback to generated
          timestamp: point.t || new Date(Date.now() + index * 60000).toISOString(), // Space out by minutes if no real timestamp
          instrument: point.instrument // Pass through per-point instrument
        };
      });
      
      /**
       * Load child bot configurations from 3 sources with priority:
       * PRIORITY 1 (Highest): bot_merge_mapping - Manual Bot Mapping tab overrides
       * PRIORITY 2 (Middle):  bot_merge_analysis_settings - Historical Analysis tab uploads
       * PRIORITY 3 (Lowest):  Published analysis config - Original analysis baseline
       * 
       * This ensures XAUUSD and other instruments configured via "Upload Trading History"
       * will appear in the Growth Chart with correct TP/SL calculations.
       */
      let realtimeGrowthPoints: GrowthDataPoint[] = [];
      if (mergedRun.config || mergedRun.parent_bot_id) {
        try {
          // PRIORITY 3: Load published config (LOWEST)
          const publishedInstrumentMap = mergedRun.config ? createInstrumentMapping(mergedRun.config) : new Map();
          const publishedSettingsMap = mergedRun.config ? createChildSettingsMap(mergedRun.config) : new Map();
          const publishedChildBotIds = mergedRun.config ? getChildBotIds(mergedRun.config, mergedRun.child_bot_ids) : [];
          
          // PRIORITY 2: Load bot_merge_analysis_settings (MIDDLE)
          const { data: analysisSettings } = await supabase
            .from('bot_merge_analysis_settings')
            .select('child_bot_id, instrument, tp_usd, sl_usd, quantity')
            .eq('parent_bot_id', mergedRun.parent_bot_id)
            .not('instrument', 'is', null);
          
          const analysisInstrumentMap = new Map<string, { childBotId: string }>();
          const analysisSettingsMap = new Map<string, { tp_usd: number; sl_usd: number; quantity: number }>();
          const analysisChildBotIds: string[] = [];
          
          analysisSettings?.forEach(s => {
            if (s.instrument && s.tp_usd && s.sl_usd && s.quantity) {
              const normalized = normalizeInstrument(s.instrument);
              analysisInstrumentMap.set(normalized, { childBotId: s.child_bot_id });
              analysisSettingsMap.set(s.child_bot_id, {
                tp_usd: s.tp_usd,
                sl_usd: s.sl_usd,
                quantity: s.quantity
              });
              if (!analysisChildBotIds.includes(s.child_bot_id)) {
                analysisChildBotIds.push(s.child_bot_id);
              }
            }
          });
          
          // PRIORITY 1: Load bot_merge_mapping (HIGHEST)
          const { data: mappingConfigs } = await supabase
            .from('bot_merge_mapping')
            .select('child_bot_id, instrument, tp_usd, sl_usd, quantity')
            .eq('parent_bot_id', mergedRun.parent_bot_id)
            .eq('is_active', true)
            .not('instrument', 'is', null);
          
          const mappingInstrumentMap = new Map<string, { childBotId: string }>();
          const mappingSettingsMap = new Map<string, { tp_usd: number; sl_usd: number; quantity: number }>();
          const mappingChildBotIds: string[] = [];
          
          mappingConfigs?.forEach(m => {
            if (m.instrument && m.tp_usd && m.sl_usd && m.quantity) {
              const normalized = normalizeInstrument(m.instrument);
              mappingInstrumentMap.set(normalized, { childBotId: m.child_bot_id });
              mappingSettingsMap.set(m.child_bot_id, {
                tp_usd: m.tp_usd,
                sl_usd: m.sl_usd,
                quantity: m.quantity
              });
              if (!mappingChildBotIds.includes(m.child_bot_id)) {
                mappingChildBotIds.push(m.child_bot_id);
              }
            }
          });
          
          // Merge with priority: Published → Analysis → Mapping (last wins)
          const finalInstrumentMap = new Map([
            ...publishedInstrumentMap, 
            ...analysisInstrumentMap, 
            ...mappingInstrumentMap
          ]);
          const finalSettingsMap = new Map([
            ...publishedSettingsMap, 
            ...analysisSettingsMap, 
            ...mappingSettingsMap
          ]);
          const allChildBotIds = [...new Set([
            ...publishedChildBotIds,
            ...analysisChildBotIds,
            ...mappingChildBotIds
          ])];
          
          console.log('Growth Chart Config Sources:', {
            fromPublished: publishedSettingsMap.size,
            fromAnalysisSettings: analysisSettings?.length || 0,
            fromMapping: mappingConfigs?.length || 0,
            totalInstruments: finalInstrumentMap.size,
            totalChildBots: finalSettingsMap.size,
            allInstruments: Array.from(finalInstrumentMap.keys()),
            allChildBotIds: allChildBotIds
          });
          
          // Create allowed bot IDs list (child bots + current bot + linked bot)
          const allowedBotIds = [...allChildBotIds];
          if (!allowedBotIds.includes(botId)) {
            allowedBotIds.push(botId);
          }
          const linkedInfo = await findLinkedBotInfo(botId);
          if (linkedInfo.linkedBotId && !allowedBotIds.includes(linkedInfo.linkedBotId)) {
            allowedBotIds.push(linkedInfo.linkedBotId);
          }
          
          if (allowedBotIds.length > 0 && baseGrowthData.length > 0) {
            const lastPoint = baseGrowthData[baseGrowthData.length - 1];
            console.log('Fetching realtime growth points with merged config:', {
              startingFromTrade: lastPoint.tradeNumber,
              allowedBotIds: allowedBotIds.length,
              instruments: Array.from(finalInstrumentMap.keys())
            });
            realtimeGrowthPoints = await this.fetchRealtimeGrowthPoints(
              allowedBotIds, 
              finalInstrumentMap,   // ✅ Merged config với XAUUSD
              finalSettingsMap,     // ✅ Merged config với XAUUSD
              lastPoint.tradeNumber,
              lastPoint.cumulative,
              lastPoint.cumulativeDollar
            );
          }
        } catch (error) {
          console.error('Error fetching realtime growth points:', error);
        }
      }
      
      // Combine base and realtime growth data
      const growthData = [...baseGrowthData, ...realtimeGrowthPoints];
      
      // Always calculate stats from growth data to ensure Max Drawdown is correct
      const calculatedStats = this.calculateStats(growthData);
      
      // If we have original stats from merge analysis, merge them but override drawdown calculations
      let stats: GrowthStats;
      if (mergedRun.stats && Object.keys(mergedRun.stats).length > 0) {
        stats = {
          // Use original stats where they exist
          finalGrowth: calculatedStats.finalGrowth,
          finalDollarAmount: calculatedStats.finalDollarAmount,
          maxGrowth: calculatedStats.maxGrowth,
          minGrowth: calculatedStats.minGrowth,
          maxDollarAmount: calculatedStats.maxDollarAmount,
          minDollarAmount: calculatedStats.minDollarAmount,
          totalTrades: growthData.length,
          wins: mergedRun.stats.wins || calculatedStats.wins,
          losses: mergedRun.stats.losses || calculatedStats.losses,
          winRate: mergedRun.stats.winRate || calculatedStats.winRate,
          totalRange: calculatedStats.totalRange,
          // Always use calculated drawdown values to ensure they're correct
          maxDrawdown: calculatedStats.maxDrawdown,
          maxDrawdownDollar: calculatedStats.maxDrawdownDollar,
          finalProfit: calculatedStats.finalDollarAmount
        };
      } else {
        stats = calculatedStats;
      }
      
      return {
        growthData,
        stats,
        childCount: mergedRun.child_bot_ids?.length || 0,
        instrument: mergedRun.instrument
      };
    } catch (error) {
      console.error('Error fetching merged growth data:', error);
      return null;
    }
  }

  /**
   * Get complete growth data for a PropBot including original and shadow variations
   */
  static async getPropBotGrowthData(
    botId: string,
    fallbackWinPercentage?: number,
    fallbackLossPercentage?: number,
    fallbackMinCapital?: number
  ): Promise<PropBotGrowthData> {
    try {
      // First check for merged analysis
      const mergedData = await this.fetchMergedGrowth(botId);
      if (mergedData) {
        return {
          originalBot: { 
            growthData: mergedData.growthData, 
            stats: mergedData.stats 
          },
          shadowBot: { 
            growthData: mergedData.growthData, 
            stats: mergedData.stats 
          },
          hasLinkedBot: true,
          linkedBotType: null,
          merged: mergedData,
          hasMergedAnalysis: true
        };
      }

      // Fall back to original logic if no merged data
      // Get prop details from original bot (handles shadow bots correctly)
      const propDetails = await this.getPropDetails(botId);
      const { winPercentage, lossPercentage, minCapital } = propDetails;

      // Get linked bot information
      const linkedBotInfo = await findLinkedBotInfo(botId);
      
      // Determine which bot has the trading file and should be the data source
      let dataSourceBotId: string;
      let dataSourceFileId: string | null = null;
      let linkedBotType: 'original' | 'shadow' | null = null;

      if (linkedBotInfo.currentBotHasFile) {
        // Current bot has the file, use it as data source
        dataSourceBotId = botId;
        const { data: currentBot } = await supabase
          .from('bots')
          .select('linked_trading_file_id')
          .eq('id', botId)
          .single();
        dataSourceFileId = currentBot?.linked_trading_file_id || null;
        linkedBotType = linkedBotInfo.currentBotType;
      } else if (linkedBotInfo.linkedBotHasFile && linkedBotInfo.linkedBotId) {
        // Linked bot has the file, use it as data source
        dataSourceBotId = linkedBotInfo.linkedBotId;
        const { data: linkedBot } = await supabase
          .from('bots')
          .select('linked_trading_file_id')
          .eq('id', linkedBotInfo.linkedBotId)
          .single();
        dataSourceFileId = linkedBot?.linked_trading_file_id || null;
        linkedBotType = linkedBotInfo.currentBotType === 'original' ? 'shadow' : 'original';
      }

      // If no bot has a linked file, generate mock data for prop bots
      if (!dataSourceFileId) {
        const originalMockData = this.generateMockGrowthData(winPercentage, lossPercentage, minCapital, false, botId);
        const shadowMockData = this.generateMockGrowthData(winPercentage, lossPercentage, minCapital, true, botId);
        return {
          originalBot: { 
            growthData: originalMockData, 
            stats: this.calculateStats(originalMockData) 
          },
          shadowBot: { 
            growthData: shadowMockData, 
            stats: this.calculateStats(shadowMockData) 
          },
          hasLinkedBot: false, // Indicate this is simulated data
          linkedBotType: null,
          hasMergedAnalysis: false
        };
      }

      // Get backtest data from the bot that has the file
      const backtestData = await this.getBacktestData(dataSourceFileId);
      
      // Get realtime data from the bot that has the file
      const realtimeData = await this.getRealtimeData(dataSourceBotId);

      // Determine inversion logic based on which bot type has the file
      const originalShouldInvert = linkedBotType === 'shadow'; // If shadow bot has file, original should invert
      const shadowShouldInvert = linkedBotType === 'original'; // If original bot has file, shadow should invert

      // Process data for both original and shadow
      const originalGrowthData = this.processGrowthData(
        backtestData,
        realtimeData,
        winPercentage,
        lossPercentage,
        minCapital,
        originalShouldInvert
      );

      const shadowGrowthData = this.processGrowthData(
        backtestData,
        realtimeData,
        winPercentage,
        lossPercentage,
        minCapital,
        shadowShouldInvert
      );

      return {
        originalBot: {
          growthData: originalGrowthData,
          stats: this.calculateStats(originalGrowthData)
        },
        shadowBot: {
          growthData: shadowGrowthData,
          stats: this.calculateStats(shadowGrowthData)
        },
        hasLinkedBot: !!linkedBotInfo.linkedBotId,
        linkedBotType,
        hasMergedAnalysis: false
      };
    } catch (error) {
      console.error('Error getting PropBot growth data:', error);
      return {
        originalBot: { growthData: [], stats: this.createEmptyStats() },
        shadowBot: { growthData: [], stats: this.createEmptyStats() },
        hasLinkedBot: false,
        linkedBotType: null,
        hasMergedAnalysis: false
      };
    }
  }

  /**
   * Get latest backtest simulation data
   */
  private static async getBacktestData(fileId: string): Promise<BacktestPosition[]> {
    try {
      const { data } = await supabase
        .from('progressive_backtest_simulations')
        .select('positions_data')
        .eq('file_id', fileId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      return (data?.positions_data as unknown as BacktestPosition[]) || [];
    } catch (error) {
      console.error('Error fetching backtest data:', error);
      return [];
    }
  }

  /**
   * Get realtime trades data
   */
  private static async getRealtimeData(botId: string): Promise<RealtimeTrade[]> {
    try {
      const { data } = await supabase
        .from('bot_realtime_trades')
        .select('predicted_result, win, timestamp')
        .eq('bot_id', botId)
        .order('timestamp', { ascending: true });

      return data || [];
    } catch (error) {
      console.error('Error fetching realtime data:', error);
      return [];
    }
  }

  /**
   * Process and combine backtest and realtime data into growth points
   */
  private static processGrowthData(
    backtestData: BacktestPosition[],
    realtimeData: RealtimeTrade[],
    winPercentage: number,
    lossPercentage: number,
    minCapital: number,
    invertResults: boolean = false
  ): GrowthDataPoint[] {
    const growthData: GrowthDataPoint[] = [];
    let cumulativeGrowth = 0;
    let cumulativeDollar = 0;
    let tradeNumber = 0;

    // Process backtest data first (filter out SKIP)
    const activeBacktestPositions = backtestData.filter(pos => pos.prediction !== 'SKIP');
    
    activeBacktestPositions.forEach((position) => {
      tradeNumber++;
      
      let isCorrect = position.isCorrect ?? false;
      let prediction = position.prediction as 'WIN' | 'LOSS';
      let actualResult = position.actualResult;

      // Apply inversion for shadow bot
      if (invertResults) {
        isCorrect = !isCorrect;
        prediction = prediction === 'WIN' ? 'LOSS' : 'WIN';
        actualResult = actualResult === 1 ? 0 : 1;
      }

      const change = isCorrect ? 1 : -1;
      const dollarChange = isCorrect 
        ? (winPercentage / 100) * minCapital 
        : -(lossPercentage / 100) * minCapital;

      cumulativeGrowth += change;
      cumulativeDollar += dollarChange;

      growthData.push({
        tradeNumber,
        growth: cumulativeGrowth,
        dollarAmount: cumulativeDollar,
        prediction,
        actual: actualResult,
        change,
        isCorrect,
        cumulative: cumulativeGrowth,
        cumulativeDollar,
        source: 'backtest'
      });
    });

    // Process realtime data (filter out SKIP)
    const activeRealtimeTrades = realtimeData.filter(trade => trade.predicted_result !== 'SKIP');
    activeRealtimeTrades.forEach((trade) => {
      tradeNumber++;
      
      let prediction = trade.predicted_result as 'WIN' | 'LOSS';
      let actualResult = trade.win;
      
      // Apply inversion for shadow bot
      if (invertResults) {
        prediction = prediction === 'WIN' ? 'LOSS' : 'WIN';
        actualResult = actualResult === 1 ? 0 : 1;
      }

      const isCorrect = (prediction === 'WIN' && actualResult === 1) || 
                       (prediction === 'LOSS' && actualResult === 0);
      
      const change = isCorrect ? 1 : -1;
      const dollarChange = isCorrect 
        ? (winPercentage / 100) * minCapital 
        : -(lossPercentage / 100) * minCapital;

      cumulativeGrowth += change;
      cumulativeDollar += dollarChange;

      growthData.push({
        tradeNumber,
        growth: cumulativeGrowth,
        dollarAmount: cumulativeDollar,
        prediction,
        actual: actualResult,
        change,
        isCorrect,
        cumulative: cumulativeGrowth,
        cumulativeDollar,
        source: 'realtime',
        timestamp: trade.timestamp
      });
    });

    return growthData;
  }

  /**
   * Calculate statistics from growth data
   */
  private static calculateStats(growthData: GrowthDataPoint[]): GrowthStats {
    if (growthData.length === 0) {
      return this.createEmptyStats();
    }

    const finalGrowth = growthData[growthData.length - 1]?.cumulative || 0;
    const finalDollarAmount = growthData[growthData.length - 1]?.cumulativeDollar || 0;
    
    const growthValues = growthData.map(d => d.cumulative);
    const dollarValues = growthData.map(d => d.cumulativeDollar);
    
    const maxGrowth = Math.max(...growthValues, 0);
    const minGrowth = Math.min(...growthValues, 0);
    const maxDollarAmount = Math.max(...dollarValues, 0);
    const minDollarAmount = Math.min(...dollarValues, 0);
    
    // Calculate proper Max Drawdown (largest drop from peak to trough)
    let maxDrawdown = 0;
    let peak = growthValues[0] || 0;
    
    for (let i = 0; i < growthValues.length; i++) {
      const currentValue = growthValues[i];
      
      // Update peak if current value is higher
      if (currentValue > peak) {
        peak = currentValue;
      }
      
      // Calculate current drawdown from peak
      const currentDrawdown = peak - currentValue;
      
      // Update max drawdown if current is larger
      if (currentDrawdown > maxDrawdown) {
        maxDrawdown = currentDrawdown;
      }
    }

    // Calculate Max Drawdown for Dollar Values (same algorithm)
    let maxDrawdownDollar = 0;
    let dollarPeak = dollarValues[0] || 0;
    
    for (let i = 0; i < dollarValues.length; i++) {
      const currentValue = dollarValues[i];
      
      // Update peak if current value is higher
      if (currentValue > dollarPeak) {
        dollarPeak = currentValue;
      }
      
      // Calculate current drawdown from peak
      const currentDrawdown = dollarPeak - currentValue;
      
      // Update max drawdown if current is larger
      if (currentDrawdown > maxDrawdownDollar) {
        maxDrawdownDollar = currentDrawdown;
      }
    }
    
    const wins = growthData.filter(d => d.isCorrect).length;
    const losses = growthData.filter(d => !d.isCorrect).length;
    const totalTrades = growthData.length;
    
    return {
      finalGrowth,
      finalDollarAmount,
      maxGrowth,
      minGrowth,
      maxDollarAmount,
      minDollarAmount,
      totalTrades,
      wins,
      losses,
      winRate: totalTrades > 0 ? (wins / totalTrades) * 100 : 0,
      totalRange: maxGrowth - minGrowth,
      maxDrawdown,
      maxDrawdownDollar,
      finalProfit: finalDollarAmount
    };
  }

  /**
   * Generate mock growth data for demonstration with consistent seed
   */
  private static generateMockGrowthData(
    winPercentage: number,
    lossPercentage: number,
    minCapital: number,
    isInverted: boolean = false,
    seed?: string
  ): GrowthDataPoint[] {
    const mockData: GrowthDataPoint[] = [];
    let cumulativeGrowth = 0;
    let cumulativeDollar = 0;
    
    // Generate 30 days of mock trading (about 60 trades)
    const totalTrades = 60;
    const baseWinRate = 0.65; // 65% win rate for demo
    
    // Create a simple seeded random function for consistent results
    const seedNum = seed ? this.hashCode(seed) : 12345;
    let randomSeed = seedNum;
    
    // Simple seeded random function
    const seededRandom = () => {
      randomSeed = (randomSeed * 9301 + 49297) % 233280;
      return randomSeed / 233280;
    };
    
    for (let i = 0; i < totalTrades; i++) {
      const tradeNumber = i + 1;
      
      // Simulate win/loss with some randomness around the base win rate
      const random = seededRandom();
      let isWin = random < baseWinRate;
      let prediction: 'WIN' | 'LOSS' = isWin ? 'WIN' : 'LOSS';
      let actualResult = isWin ? 1 : 0;
      
      // Apply inversion for shadow bot
      if (isInverted) {
        isWin = !isWin;
        prediction = prediction === 'WIN' ? 'LOSS' : 'WIN';
        actualResult = actualResult === 1 ? 0 : 1;
      }
      
      const isCorrect = (prediction === 'WIN' && actualResult === 1) || 
                       (prediction === 'LOSS' && actualResult === 0);
      
      const change = isCorrect ? 1 : -1;
      const dollarChange = isCorrect 
        ? (winPercentage / 100) * minCapital 
        : -(lossPercentage / 100) * minCapital;

      cumulativeGrowth += change;
      cumulativeDollar += dollarChange;

      // Generate timestamp (last 30 days)
      const timestamp = new Date();
      timestamp.setDate(timestamp.getDate() - Math.floor((totalTrades - i) / 2));
      
      mockData.push({
        tradeNumber,
        growth: cumulativeGrowth,
        dollarAmount: cumulativeDollar,
        prediction,
        actual: actualResult,
        change,
        isCorrect,
        cumulative: cumulativeGrowth,
        cumulativeDollar,
        source: 'backtest', // Mark as simulated
        timestamp: timestamp.toISOString()
      });
    }
    
    return mockData;
  }

  /**
   * Create empty stats object
   */
  private static createEmptyStats(): GrowthStats {
    return {
      finalGrowth: 0,
      finalDollarAmount: 0,
      maxGrowth: 0,
      minGrowth: 0,
      maxDollarAmount: 0,
      minDollarAmount: 0,
      totalTrades: 0,
      wins: 0,
      losses: 0,
      winRate: 0,
      totalRange: 0,
      maxDrawdown: 0,
      maxDrawdownDollar: 0,
      finalProfit: 0
    };
  }

  /**
   * Simple hash function for seeding
   */
  private static hashCode(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
  }
}